import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { salesService } from '../services';

const SalesContext = createContext();

export const useSales = () => {
    const context = useContext(SalesContext);
    if (!context) {
        throw new Error('useSales must be used within a SalesProvider');
    }
    return context;
};

export const SalesProvider = ({ children }) => {
    const [sales, setSales] = useState([]);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Load sales from localStorage on mount
    useEffect(() => {
        const loadInitialData = async () => {
            try {
                setLoading(true);
                setError(null);
                
                // Try to fetch from API first
                try {
                    const [salesData, statsData] = await Promise.all([
                        salesService.getAll(),
                        salesService.getStats()
                    ]);
                    
                    if (Array.isArray(salesData)) {
                        setSales(salesData);
                    } else {
                        console.warn('API returned non-array data:', salesData);
                        setSales([]);
                    }
                    
                    if (statsData) {
                        setStats(statsData);
                    }
                } catch (apiError) {
                    console.warn('API call failed, falling back to local storage:', apiError);
                    // Fall back to local storage
                    const storedSales = localStorage.getItem('salesTrackerDataReact');
                    if (storedSales) {
                        try {
                            const parsedSales = JSON.parse(storedSales);
                            setSales(Array.isArray(parsedSales) ? parsedSales : []);
                        } catch (e) {
                            console.error("Error parsing sales data from localStorage:", e);
                            localStorage.removeItem('salesTrackerDataReact');
                            setSales([]);
                        }
                    } else {
                        setSales([]);
                    }
                }
            } catch (err) {
                console.error("Error loading initial sales data:", err);
                setError(err.message);
                setSales([]);
            } finally {
                setLoading(false);
            }
        };

        loadInitialData();
    }, []);

    // Save sales to localStorage whenever they change
    useEffect(() => {
        if (Array.isArray(sales)) {
            localStorage.setItem('salesTrackerDataReact', JSON.stringify(sales));
        }
    }, [sales]);

    // Create a new sale
    const createSale = useCallback(async (saleData) => {
        try {
            setLoading(true);
            setError(null);
            
            // Try to make API call to create sale
            try {
                const newSale = await salesService.create(saleData);
                // Refresh the entire sales list and stats to get the latest data with product information
                const [updatedSales, updatedStats] = await Promise.all([
                    salesService.getAll(),
                    salesService.getStats()
                ]);
                
                if (Array.isArray(updatedSales)) {
                    setSales(updatedSales);
                } else {
                    // Fallback to adding just the new sale
                    setSales(prevSales => {
                        const currentSales = Array.isArray(prevSales) ? prevSales : [];
                        return [newSale, ...currentSales];
                    });
                }
                
                if (updatedStats) {
                    setStats(updatedStats);
                }
                
                return newSale;
            } catch (apiError) {
                console.warn('API call failed, falling back to local storage:', apiError);
                // Fall back to local storage
                const newSale = {
                    ...saleData,
                    _id: `local_${Date.now()}`, // Use _id to match MongoDB format
                    date: new Date().toLocaleDateString()
                };
                setSales(prevSales => {
                    const currentSales = Array.isArray(prevSales) ? prevSales : [];
                    return [newSale, ...currentSales];
                });
                return newSale;
            }
        } catch (err) {
            setError(err.message || 'Failed to create sale');
            throw err;
        } finally {
            setLoading(false);
        }
    }, []);

    // Get all sales
    const getSales = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            
            // Try to fetch from API
            try {
                const [salesData, statsData] = await Promise.all([
                    salesService.getAll(),
                    salesService.getStats()
                ]);
                
                if (Array.isArray(salesData)) {
                    setSales(salesData);
                } else {
                    console.warn('API returned non-array data:', salesData);
                    setSales([]);
                }
                
                if (statsData) {
                    setStats(statsData);
                }
                
                return Array.isArray(salesData) ? salesData : [];
            } catch (apiError) {
                console.warn('API call failed, using local storage data:', apiError);
                // Return local storage data
                const currentSales = Array.isArray(sales) ? sales : [];
                return currentSales;
            }
        } catch (err) {
            setError(err.message || 'Failed to fetch sales');
            throw err;
        } finally {
            setLoading(false);
        }
    }, [sales]);

    // Delete a sale
    const deleteSale = useCallback(async (saleId) => {
        if (!saleId) {
            console.error('No sale ID provided for deletion');
            throw new Error('No sale ID provided');
        }

        try {
            setLoading(true);
            setError(null);
            
            // Check if it's a local sale
            const isLocalSale = typeof saleId === 'string' && saleId.startsWith('local_');
            
            if (!isLocalSale) {
                // Try to delete from API
                try {
                    await salesService.delete(saleId);
                } catch (apiError) {
                    console.warn('API call failed, deleting from local storage only:', apiError);
                }
            }
            
            // Update local state by removing the deleted sale
            setSales(prevSales => {
                const currentSales = Array.isArray(prevSales) ? prevSales : [];
                return currentSales.filter(sale => sale._id !== saleId);
            });
        } catch (err) {
            setError(err.message || 'Failed to delete sale');
            throw err;
        } finally {
            setLoading(false);
        }
    }, []);

    const value = {
        sales: Array.isArray(sales) ? sales : [],
        stats,
        loading,
        error,
        createSale,
        getSales,
        deleteSale
    };

    return (
        <SalesContext.Provider value={value}>
            {children}
        </SalesContext.Provider>
    );
};

export default SalesContext; 